/**
 * App State Hook
 * Handles app state changes (background/foreground) for mobile
 * Re-validates authentication when app comes to foreground
 * 
 * OPTIMIZED: Respects rate limiting from AuthContext
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const MIN_REVALIDATE_INTERVAL = 30 * 1000; // 30 seconds minimum between revalidations

/**
 * Hook to handle app state changes and re-validate authentication
 * On mobile: listens to AppState changes
 * On web: listens to visibility changes
 */
export const useAppState = () => {
  const { revalidateAuth } = useAuth();
  const lastRevalidateTime = useRef(0);

  // Rate-limited revalidation wrapper
  const revalidateIfNeeded = () => {
    const now = Date.now();
    const timeSinceLastRevalidate = now - lastRevalidateTime.current;

    if (timeSinceLastRevalidate < MIN_REVALIDATE_INTERVAL) {
      console.log(`⏱️  useAppState: Skipping revalidation (too soon - ${timeSinceLastRevalidate}ms ago)`);
      return;
    }

    console.log('🔄 useAppState: Triggering revalidation');
    lastRevalidateTime.current = now;
    revalidateAuth();
  };

  useEffect(() => {
    // Check if we're in a React Native environment
    const isReactNative = typeof navigator !== 'undefined' && 
                         (navigator as any).product === 'ReactNative';

    let appStateListener: any = null;
    let visibilityCleanup: (() => void) | null = null;

    if (isReactNative) {
      // React Native: Use AppState
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { AppState } = require('react-native');
        appStateListener = AppState.addEventListener('change', (nextAppState: string) => {
          if (nextAppState === 'active') {
            console.log('📱 App came to foreground');
            revalidateIfNeeded();
          }
        });
      } catch (error) {
        // AppState not available (web environment) - expected
      }
    } else {
      // Web: Use visibility API
      if (typeof document !== 'undefined') {
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            console.log('🌐 Tab became visible');
            revalidateIfNeeded();
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        visibilityCleanup = () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
      }
    }

    // REMOVED: Initial validation on mount
    // AuthContext already handles initial auth check
    // No need to duplicate it here

    // Cleanup
    return () => {
      if (appStateListener) {
        appStateListener.remove();
      }
      if (visibilityCleanup) {
        visibilityCleanup();
      }
    };
  }, []); // ← FIXED: Empty dependency array, only runs once
};